// ported from main.tsx — i18next init (embedded JSON resources, useSuspense: false)
import "../i18n/config";

import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  ClientOnly,
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, type ReactNode } from "react";

import i18n from "../i18n/config";
import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
// react-cookie-consent is a browser-only CJS package whose default-import
// interop crashes SSR ("Element type is invalid: got object") — load it
// lazily behind ClientOnly so it never enters the SSR module graph.
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
import ContextualDiscovery from "@/components/ContextualDiscovery";
import Breadcrumbs from "@/components/Layout/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { initGA, logPageView } from "@/lib/analytics";
import { reportLovableError } from "@/lib/lovable-error-reporting";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      {
        httpEquiv: "Content-Security-Policy",
        content:
          "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com; style-src 'self' 'unsafe-inline'; img-src 'self' data: https: blob:; font-src 'self'; connect-src 'self' https://www.google-analytics.com https://region1.google-analytics.com https://dpuwbwtckkcbtuzmkflz.supabase.co wss://dpuwbwtckkcbtuzmkflz.supabase.co;",
      },
      { name: "fragment", content: "!" },
      { title: "Nearshore.Africa | Software Outsourcing Africa" },
      {
        name: "description",
        content:
          "Save up to 60% on software development costs with skilled African developers. Same timezone, English-speaking talent.",
      },
      {
        name: "keywords",
        content:
          "nearshore outsourcing Africa, software development Africa, Egypt developers, Kenya tech talent, African software companies, Evolvice, IT outsourcing, nearshoring vs offshoring",
      },
      { name: "author", content: "Nearshore.Africa" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://nearshore.africa" },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/411359fa-86d7-4a0d-8dde-fa1591b6c690",
      },
      { property: "og:title", content: "Nearshore.Africa | Software Outsourcing Africa" },
      {
        property: "og:description",
        content:
          "Save up to 60% on software development costs with skilled African developers. Same timezone, English-speaking talent.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/411359fa-86d7-4a0d-8dde-fa1591b6c690",
      },
      { name: "twitter:title", content: "Nearshore.Africa | Software Outsourcing Africa" },
      {
        name: "twitter:description",
        content:
          "Save up to 60% on software development costs with skilled African developers. Same timezone, English-speaking talent.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "canonical", href: "https://nearshore.africa" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => <NotFound />,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// Analytics component to track page views (ported from App.tsx)
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    logPageView();
  }, [location.pathname, location.search]);

  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { pathname } = useLocation();

  // Keep i18n language in sync with the URL on both server and client so
  // German routes server-render in German (URL is the source of truth).
  const lang = pathname === "/en" || pathname.startsWith("/en/") ? "en" : "de";
  if (i18n.language !== lang) {
    void i18n.changeLanguage(lang);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AnalyticsTracker />
        <Breadcrumbs />
        <ClientOnly fallback={null}>
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
        </ClientOnly>
        <ContextualDiscovery />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">This page didn't load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </Button>
          <Button variant="outline" asChild>
            <a href="/">Go home</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
